import { CheckCircleFilled, CheckCircleOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import IconButton from 'components/@extended/IconButton';
import { PopupTransition } from 'components/@extended/Transitions';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { Row } from 'react-table';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import TableDialogSelectorProps from './dto';
import SelectedItemCard from './selected-item-card';

const TableDialogSelector = <T extends {}>({
    name,
    dialogTitle,
    defaultVisibleColumnConfig,
    defaultTableColumnConfig,
    searchableTableColumnConfig,
    multiple = false,
    getSelectedItemTitle,
    getSelectedItemSubtitle,
    getSelectedItemImage,
    fetchDataCb,
}: TableDialogSelectorProps<T>) => {
    const [field, meta, helpers] = useField(name);

    const [openContactSelectDialog, setOpenContactSelectDialog] = useState(false);
    const [contactList, setContactList] = useState<Array<T>>([]);
    const [pageCount, setPageCount] = useState(0);

    const defaultColumnConfig = useMemo(
        () => [
            ...defaultTableColumnConfig,
            {
                Header: 'Select',
                Cell: ({ row }: { row: Row }) => (
                    <IconButton
                        variant="outlined"
                        color="info"
                        onClick={() => {
                            const selectedItem = row.original as any;
                            if (!multiple) {
                                helpers.setValue(selectedItem);
                                setOpenContactSelectDialog(false);
                            } else {
                                if (field.value.findIndex((item: any) => item.id === selectedItem.id) === -1)
                                    helpers.setValue([...field.value, selectedItem]);
                                else {
                                    helpers.setValue(field.value.filter((i: any) => i.id !== selectedItem.id));
                                }
                            }
                        }}
                    >
                        {multiple &&
                        field.value.findIndex((item: any) => item.id === (row.original as any).id) !== -1 ? (
                            <CheckCircleFilled />
                        ) : (
                            <CheckCircleOutlined />
                        )}
                    </IconButton>
                ),
            },
        ],
        [defaultTableColumnConfig, field.value, helpers, multiple],
    );

    const fetchData: BaseTableFetchFuncProps = async ({ pageSize, pageIndex, filters, searchTerm }) => {
        const data = await fetchDataCb({
            pageIndex,
            pageSize,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, searchableTableColumnConfig)
                    : undefined,
        });

        if (data) {
            setContactList(data.elements);
            setPageCount(Math.ceil(data.total / pageSize));
        }
    };

    const renderDisplayItems = () => {
        if (!multiple) {
            if (field.value)
                return (
                    <SelectedItemCard
                        title={getSelectedItemTitle(field.value)}
                        subTitle={getSelectedItemSubtitle?.(field.value)}
                        image={getSelectedItemImage?.(field.value)}
                        onRemove={() => helpers.setValue(null)}
                    />
                );

            return (
                <Button
                    variant="dashed"
                    color="secondary"
                    onClick={() => setOpenContactSelectDialog(true)}
                    sx={{ height: 72 }}
                >
                    <PlusOutlined style={{ fontSize: '1.3rem' }} />
                </Button>
            );
        }

        return (
            <Grid container rowGap={1.5} columnGap={1}>
                {field.value.map((item: any) => (
                    <Grid item xs={12} sm={5}>
                        <SelectedItemCard
                            title={getSelectedItemTitle(item)}
                            subTitle={getSelectedItemSubtitle?.(item)}
                            image={getSelectedItemImage?.(item)}
                            onRemove={() => helpers.setValue(field.value.filter((i: any) => i !== item))}
                        />
                    </Grid>
                ))}
                <Grid item xs={12} sm={5}>
                    <Button
                        variant="dashed"
                        color="secondary"
                        onClick={() => setOpenContactSelectDialog(true)}
                        sx={{ height: 72, width: '100%' }}
                    >
                        <PlusOutlined style={{ fontSize: '1.3rem' }} />
                    </Button>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {meta.touched && meta.error && <Typography color="red">{meta.error}</Typography>}
            {renderDisplayItems()}
            <Dialog
                maxWidth="xl"
                TransitionComponent={PopupTransition}
                fullWidth
                open={openContactSelectDialog}
                sx={{ maxHeight: '100vh', '& .MuiDialog-paper': { p: 0 } }}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                    {dialogTitle || 'Select entity'}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpenContactSelectDialog(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseOutlined />
                </IconButton>
                <DialogContent dividers>
                    <ScrollX>
                        <BaseTable
                            data={contactList}
                            fetchData={fetchData}
                            pageCount={pageCount}
                            defaultColumnConfig={defaultColumnConfig}
                            defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                        />
                    </ScrollX>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TableDialogSelector;
